export const piarcHero = {
  badge: "Applied Research Programme",
  title: "Partnership for Implementation of Applied Research Circles",
  subtitle:
    "PIARC brings together academics, factory engineers, and industry practitioners in applied research circles. Each circle focuses on a live production problem and produces an implementable research output within 6 months.",
  ctaPrimary: "Start a Research Circle",
  ctaSecondary: "Explore the Process",
};

export const piarcWhatIs = {
  title: "What is PIARC?",
  paragraphs: [
    "PIARC - the Partnership for Implementation of Applied Research Circles - is TIE's research wing programme that converts real factory challenges into validated, implementable solutions through structured, evidence-led investigation.",
    "Unlike academic research that may never leave the lab, PIARC is designed for immediate factory applicability. Each research circle brings together three perspectives: academic researchers who design rigorous methodologies, factory engineers who provide production data and test environments, and industry practitioners who ensure findings are commercially viable.",
    "Research circles run for 6 months with defined milestones. The output is not just a research paper — it is an implementation guide, a validated protocol, and a case study that other factories can replicate. PIARC answers the question: what works, why it works, and how to do it in your factory.",
  ],
  highlights: [
    { label: "Duration", value: "6 Months" },
    { label: "Perspectives", value: "3" },
    { label: "Output", value: "Implementable" },
    { label: "Circles Active", value: "12+" },
  ],
};

export const piarcResearchAreas = [
  {
    name: "Process Optimization",
    icon: "ri-settings-3-line",
    examples: [
      "Water consumption reduction in jet dyeing",
      "Steam efficiency in finishing lines",
      "Chemical dosing accuracy improvement",
      "Batch consistency in color reproduction",
    ],
  },
  {
    name: "Automation & Smart Manufacturing",
    icon: "ri-cpu-line",
    examples: [
      "Defect detection using machine vision",
      "Sewing line balancing algorithms",
      "Predictive maintenance for knitting machines",
      "IoT-based energy monitoring systems",
    ],
  },
  {
    name: "Sustainability & Circularity",
    icon: "ri-recycle-line",
    examples: [
      "Pre-consumer waste recycling pathways",
      "Biodegradable finishing chemical alternatives",
      "Carbon footprint mapping per garment type",
      "Closed-loop water system design",
    ],
  },
  {
    name: "Material Innovation",
    icon: "ri-stack-line",
    examples: [
      "MMF blend ratio optimization for hand-feel",
      "New fiber performance benchmarking",
      "Technical textile weave structure development",
      "Natural dye fastness improvement protocols",
    ],
  },
  {
    name: "Quality & RFT Improvement",
    icon: "ri-award-line",
    examples: [
      "Defect clustering analysis in knitting",
      "Shrinkage control in wet processing",
      "Color variation root-cause mapping",
      "Dimensional stability enhancement",
    ],
  },
  {
    name: "Human Factors & Ergonomics",
    icon: "ri-team-line",
    examples: [
      "Operator fatigue impact on quality",
      "Workstation design for productivity",
      "Training method effectiveness comparison",
      "Multi-skill operator performance tracking",
    ],
  },
];

export const piarcProcessSteps = [
  {
    phase: "Phase 1",
    week: "Month 1",
    title: "Problem Selection & Team Formation",
    description:
      "A live production problem is identified and validated for research suitability. A cross-functional team of academics, factory engineers, and practitioners is formed and aligned on objectives.",
    activities: [
      "Factory problem identification and prioritization",
      "Research feasibility and ethics assessment",
      "Cross-functional team formation and onboarding",
      "Literature review and baseline data collection",
    ],
  },
  {
    phase: "Phase 2",
    week: "Months 2–3",
    title: "Hypothesis & Experimental Design",
    description:
      "The team formalizes research hypotheses, designs experiments with control variables, and establishes data collection protocols that can be executed on the factory floor without disrupting production.",
    activities: [
      "Hypothesis formulation and variable mapping",
      "Experimental design with controls",
      "Data collection protocol development",
      "Factory production integration planning",
    ],
  },
  {
    phase: "Phase 3",
    week: "Months 4–5",
    title: "Data Collection & Analysis",
    description:
      "Experiments run under live production conditions. Data is collected systematically, cleaned, and analyzed using appropriate statistical methods. Preliminary findings are reviewed with factory leadership.",
    activities: [
      "Live production data collection",
      "Statistical analysis and significance testing",
      "Preliminary findings review with stakeholders",
      "Protocol refinement based on early results",
    ],
  },
  {
    phase: "Phase 4",
    week: "Month 6",
    title: "Implementation Guide & Publication",
    description:
      "Final outputs include a peer-validated research report, a factory-ready implementation guide, and a case study for sector-wide learning. Findings are presented at AIRCon and submitted to IJTAI.",
    activities: [
      "Research report with validated findings",
      "Factory implementation guide with SOPs",
      "Case study for sector publication",
      "AIRCon presentation and IJTAI submission",
    ],
  },
];

export const piarcOutcomes = {
  title: "What Every PIARC Circle Produces",
  items: [
    {
      title: "Peer-Validated Research Report",
      description:
        "A rigorous research document with methodology, data analysis, findings, and limitations — reviewed by both academic and industry peers before publication.",
      icon: "ri-file-list-3-line",
    },
    {
      title: "Factory Implementation Guide",
      description:
        "A practical, step-by-step guide for replicating the solution in other factories, including resource requirements, timelines, and expected outcomes.",
      icon: "ri-book-open-line",
    },
    {
      title: "Published Case Study",
      description:
        "A sector-facing case study documenting the problem, approach, results, and lessons — published through TIE channels and submitted to IJTAI.",
      icon: "ri-article-line",
    },
    {
      title: "AIRCon Presentation Slot",
      description:
        "Completed PIARC circles receive a guaranteed presentation slot at the next Annual International Research Conference, sharing findings with the full ecosystem.",
      icon: "ri-presentation-line",
    },
    {
      title: "Factory-Tested Protocol",
      description:
        "Every recommendation has been tested under real production conditions, not simulated or theoretical — giving factories confidence to adopt.",
      icon: "ri-shield-check-line",
    },
    {
      title: "Cross-Border Collaboration",
      description:
        "PIARC teams often include international researchers and global technology partners, creating knowledge transfer that benefits the entire Bangladesh T&A sector.",
      icon: "ri-global-line",
    },
  ],
};

export const piarcStats = [
  { value: "12+", label: "Active Circles", sub: "across 8 factories" },
  { value: "6", label: "Months", sub: "per research cycle" },
  { value: "3", label: "Perspectives", sub: "academic, factory, industry" },
  { value: "100%", label: "Factory-Tested", sub: "every output validated on floor" },
];